import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { FiCamera, FiShare2, FiCopy, FiCheck, FiUsers } from "react-icons/fi";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import api from "../api";
import ReactGA from "react-ga4";
import { useAuth } from "../AuthContext";
import { useTranslation } from "react-i18next";
import HeaderAdmin from "../components/HeaderAdmin";
import EventPublicNav from "../components/EventPublicNav";
import PoweredBy from "../components/PoweredBy";
import SocialFeed from "../components/SocialFeed";
import SocialUploadModal from "../components/SocialUploadModal";
import LoginPromptModal from "../components/LoginPromptModal";
import FollowersModal from "../components/FollowersModal";
import "./SocialGalleryPage.css";
import { socialNotificationApi } from "../services/socialNotificationApi";

export default function SocialGalleryPage() {
  const { id_name } = useParams();
  const { authenticated, user, logout } = useAuth();
  const { i18n } = useTranslation();

  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [newPhoto, setNewPhoto] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const [followerCount, setFollowerCount] = useState(null);
  const [showFollowers, setShowFollowers] = useState(false);
  const shareRef = useRef(null);

  const socialUrl = event ? `${window.location.origin}/event/${event.id_name}/social` : "";
  const qrUrl = event ? `${process.env.REACT_APP_MODEL_ENDPOINT}/qr?eid=${event.event_id}&type=social` : "";

  function handleCopy() {
    navigator.clipboard.writeText(socialUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  // Close share sheet on outside click
  useEffect(() => {
    if (!showShare) return;
    function handleOutside(e) {
      if (shareRef.current && !shareRef.current.contains(e.target)) setShowShare(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [showShare]);

  // Track guest wall visit once per page load
  useEffect(() => {
    ReactGA.event({ category: "guest", action: "guest_wall_viewed" });
  }, []);

  // Fetch event by id_name, then auto-follow if authenticated + fetch follower count
  useEffect(() => {
    async function fetchEvent() {
      try {
        const res = await api.get(`/check_eid?id_name=${id_name}`);
        const ev = res.data.event ?? null;
        setEvent(ev);
        if (ev?.language) i18n.changeLanguage(ev.language);
        if (ev) {
          if (authenticated) {
            api.post("/followers/follow", { event_id: ev.event_id }).catch(() => {});
            socialNotificationApi.markRead(ev.event_id).catch(() => {});
          }
          api.get(`/followers/count?eid=${ev.event_id}`)
            .then((r) => setFollowerCount(r.data.count ?? null))
            .catch(() => {});
        }
      } catch {
        setEvent(null);
      } finally {
        setLoadingEvent(false);
      }
    }
    if (id_name) fetchEvent();
  }, [id_name, authenticated]);

  function handleUploadClick() {
    if (!authenticated) {
      setShowLoginPrompt(true);
    } else {
      setShowUpload(true);
    }
  }

  function handleUploaded(photo) {
    setNewPhoto(photo);
    setShowUpload(false);
  }

  if (loadingEvent) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (!event) {
    return (
      <div style={{ textAlign: "center", padding: 64, color: "#8e8e8e" }}>
        Event not found.
      </div>
    );
  }

  return (
    <>
      {authenticated && (
        <HeaderAdmin
          page_name={event?.name ?? ""}
          isAuthenticated={authenticated}
          onLogout={logout}
          user={user}
        />
      )}
      <EventPublicNav />
    <div className="social-gallery-page event-page-content">
      {/* Page header */}
      <div className="social-page-header">
        <div className="social-page-header__info">
          <h1 className="social-page-header__title">{event.name}</h1>
          <p className="social-page-header__sub">
            Event Wall
            {followerCount !== null && (
              <button className="social-follower-count social-follower-count--btn" onClick={() => setShowFollowers(true)}>
                <FiUsers size={11} />
                {followerCount}
              </button>
            )}
          </p>
        </div>
        <div className="social-page-header__actions">
          {/* Share button */}
          <div className="social-share-wrapper" ref={shareRef}>
            <button
              className="social-page-header__share-btn"
              onClick={() => setShowShare((v) => !v)}
              aria-label="Share"
              title="Share this gallery"
            >
              <FiShare2 size={17} />
            </button>

            {showShare && (
              <div className="social-share-sheet">
                {/* QR code */}
                <img src={qrUrl} alt="QR code" className="social-share-sheet__qr" />

                {/* URL display */}
                <p className="social-share-sheet__url">{socialUrl}</p>

                {/* Actions */}
                <div className="social-share-sheet__btns">
                  <button className="social-share-sheet__btn" onClick={handleCopy}>
                    {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                    {copied ? "Copied!" : "Copy link"}
                  </button>
                  <a
                    className="social-share-sheet__btn social-share-sheet__btn--wa"
                    href={`https://wa.me/?text=${encodeURIComponent(`📸 Share your photos! ${socialUrl}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FontAwesomeIcon icon={faWhatsapp} size="sm" />
                    WhatsApp
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Upload CTA */}
          <button className="social-page-header__cta" onClick={handleUploadClick}>
            <FiCamera size={16} />
            <span>Add a Photo</span>
          </button>
        </div>
      </div>

      {/* Feed */}
      <SocialFeed
        eventId={event.event_id}
        authenticated={authenticated}
        currentUserId={user?.user_id ?? null}
        onLoginRequired={() => setShowLoginPrompt(true)}
        newPhoto={newPhoto}
      />

      <PoweredBy />

      {/* Upload modal */}
      <SocialUploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        eventId={event.event_id}
        onUploaded={handleUploaded}
      />

      {/* Login prompt */}
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        source="guest_wall"
      />
      <FollowersModal
        isOpen={showFollowers}
        onClose={() => setShowFollowers(false)}
        eventId={event?.event_id}
        eventName={event?.name}
      />
    </div>
    </>
  );
}
